import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  ColorPicker,
  ColorSwatch,
  Group,
  Input,
  Modal,
  Select,
  Stack,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { sortBy } from "lodash-es";
import { z } from "zod";

import {
  getGetDepartmentsOptionallyfilteredbylocationQueryKey,
  useCreateDepartment,
} from "@/shared/api/generated/departments";
import { useGetLocations } from "@/shared/api/generated/locations";
import { useFormWatch } from "@/shared/utils/forms/useFormWatch";

function useFormSchema() {
  const { t } = useLingui();

  return z.object({
    name: z.string().min(1, { message: t`Navn er obligatorisk` }),
    locationId: z.string().min(1, { message: t`Velg en lokasjon` }),
    calendarColor: z.string().optional(),
  });
}

type FormSchema = z.infer<ReturnType<typeof useFormSchema>>;

export default function CreateDepartment({ museumId }: { museumId?: string }) {
  const { t } = useLingui();

  const [opened, { open, close }] = useDisclosure(false);

  const formSchema = useFormSchema();

  const form = useForm<FormSchema>({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      locationId: museumId ?? "",
      calendarColor: "",
    },
    validate: zodResolver(formSchema),
  });

  const selectedColor = useFormWatch(form, "calendarColor");

  const queryClient = useQueryClient();

  const { mutateAsync: createDepartment } = useCreateDepartment();

  async function handleSubmit(values: FormSchema) {
    await createDepartment(
      {
        data: {
          name: values.name,
          locationId: values.locationId,
          calendarColor: values.calendarColor ?? null,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getGetDepartmentsOptionallyfilteredbylocationQueryKey(),
          });
          close();
        },
      },
    );
  }

  const { data: locations } = useGetLocations();

  const theme = useMantineTheme();

  // Calendar event colors - using medium saturation shades (5-6) for good visibility and contrast
  const calendarColors = [
    theme.colors.blue[5],
    theme.colors.indigo[5],
    theme.colors.cyan[5],
    theme.colors.teal[5],
    theme.colors.green[5],
    theme.colors.lime[5],
    theme.colors.yellow[5],
    theme.colors.orange[5],
    theme.colors.red[5],
    theme.colors.pink[5],
    theme.colors.grape[5],
    theme.colors.violet[5],
    theme.colors.primary[5],
  ];

  return (
    <>
      <Button onClick={open} leftSection={<IconPlus />}>
        <Trans>Opprett avdeling</Trans>
      </Button>

      <Modal
        opened={opened}
        onClose={close}
        title={
          <Title size="sm">
            <Trans>Opprett avdeling</Trans>
          </Title>
        }
        transitionProps={{
          onExited: () => form.reset(),
        }}
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label={t`Navn`}
              placeholder={t`Velg navn`}
              withAsterisk
              {...form.getInputProps("name")}
            />

            {!museumId && (
              <Select
                label={t`Lokasjon`}
                placeholder={t`Velg lokasjon`}
                data={sortBy(
                  locations?.data?.map((location) => ({
                    value: location.id,
                    label: location.name,
                  })),
                  (o) => o.label.toLowerCase(),
                )}
                searchable
                withAsterisk
                {...form.getInputProps("locationId")}
              />
            )}

            <Group align="flex-start" justify="space-between">
              <Input.Wrapper
                label={t`Farge`}
                description={t`Fargen som velges blir synlig i kalendern`}
              >
                <ColorPicker
                  {...form.getInputProps("calendarColor")}
                  withPicker={false}
                  swatches={calendarColors}
                  onColorSwatchClick={(color) => {
                    form.setFieldValue(
                      "calendarColor",
                      selectedColor === color ? "" : color,
                    );
                  }}
                />
              </Input.Wrapper>

              <Input.Wrapper label={t`Forhåndsvisning`}>
                <ColorSwatch
                  mt={8}
                  size={64}
                  style={{ "--cs-radius": "var(--mantine-radius-sm)" }}
                  color={selectedColor ?? ""}
                />
              </Input.Wrapper>
            </Group>

            <Group justify="end" gap={8} mt={8}>
              <Button variant="subtle" onClick={close}>
                <Trans>Avbryt</Trans>
              </Button>
              <Button type="submit">
                <Trans>Opprett</Trans>
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
